import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, DialogActions, Stack, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

interface PopupProps {
  open: boolean;
  setOpen: (val: boolean) => void;
}

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

const Popup1099StateAl = ({ open, setOpen }: PopupProps) => {
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
      className=""
    >
      <BootstrapDialogTitle
        id="customized-dialog-title"
        onClose={handleClose}
      />
      <DialogContent className="popup-bg">
        <Box
          component="div"
          sx={{ textAlign: 'center', mt: 2, borderBottom: '1px solid #ccc' }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2, md: 3 }}
            alignItems="center"
            justifyContent="space-around"
            sx={{ px: { xs: 0, sm: 2 }, display: 'flex' }}
          >
            <Box sx={{ textAlign: { xs: 'center', sm: 'center' } }}>
              <picture>
                <img
                  src="/assets/images/state-flag/alabama-flag.svg"
                  loading="lazy"
                  alt="Alabama State Filing Requirements"
                  className="tab-responsive"
                  width={120}
                  height={50}
                />
              </picture>
            </Box>
            <Box>
              <Typography
                variant="body1"
                color="primary"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: 22, sm: 24, md: 26 },
                  color: '#333',
                  lineHeight: 1.2,
                  mb: 2,
                  textAlign: 'center',
                }}
              >
                Alabama State Filing Requirements
              </Typography>
            </Box>
          </Stack>
        </Box>
        <Box component="div" sx={{ textAlign: 'left', mt: 2 }}>
          <Typography
            variant="body1"
            sx={{ fontSize: { xs: 18, sm: 20, md: 22 }, fontWeight: 600 }}
          >
            Alabama requires
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2, md: 4 }}
          >
            <Box
              component="ul"
              sx={{ listStyleType: 'none', pl: { xs: 4, sm: 2 }, mb: 1 }}
            >
              <Box
                component="li"
                sx={{
                  display: { xs: 'inline-block', sm: 'flex' },
                  alignItems: 'center',
                  mb: 1.25,
                  textIndent: { xs: '-30px', sm: 0 },
                  textAlign: 'left',
                }}
              >
                <CheckCircleRoundedIcon
                  sx={{
                    mr: 1,
                    color: '#1da94a',
                    mb: { xs: -0.8, sm: 0 },
                  }}
                />
                1099-NEC, 1099-MISC, 1099-B, 1099-DIV, 1099-G, 1099-INT, 1099-K,
                1099-OID, 1099-R, W-2G
              </Box>
              <Box
                component="li"
                sx={{
                  display: { xs: 'inline-block', sm: 'flex' },
                  alignItems: 'flex-start',
                  mb: 1.25,
                  textIndent: { xs: '-30px', sm: 0 },
                  textAlign: 'left',
                }}
              >
                <CheckCircleRoundedIcon
                  sx={{
                    mr: 1,
                    color: '#1da94a',
                    mb: { xs: -0.8, sm: 0 },
                  }}
                />
                Form A-3 (Annual Reconciliation)
              </Box>
            </Box>
            <Box sx={{ textAlign: { xs: 'center', sm: 'center' } }}>
              <picture>
                <img
                  src="/assets/images/alabama-img.webp"
                  loading="lazy"
                  alt="Alabama State Filing Requirements"
                  className="tab-responsive"
                  width={105}
                  height={105}
                />
              </picture>
            </Box>
          </Stack>
        </Box>
        <Box component="div" sx={{ borderTop: '1px solid #ccc' }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: 18, sm: 20, md: 22 },
              fontWeight: 600,
              mt: 2,
            }}
          >
            Filing Deadlines for 2024 Tax Year
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'column' }}
            spacing={{ xs: 1, sm: 2, md: 4 }}
          >
            <Box
              component="ul"
              sx={{ listStyleType: 'none', pl: { xs: 4, sm: 2 }, mb: 1 }}
            >
              <Box
                component="li"
                sx={{
                  display: { xs: 'inline-block', sm: 'flex' },
                  alignItems: 'center',
                  mb: 1.25,
                  textIndent: { xs: '-30px', sm: 0 },
                  textAlign: 'left',
                }}
              >
                <CheckCircleRoundedIcon
                  sx={{
                    mr: 1,
                    color: '#1da94a',
                    mb: { xs: -0.8, sm: 0 },
                  }}
                />
                With State Income Tax Withheld:&nbsp;
                <span style={{ fontWeight: 700, color: '#da4b4b' }}>
                  January 31, 2025
                </span>
              </Box>
              <Box
                component="li"
                sx={{
                  display: { xs: 'inline-block', sm: 'flex' },
                  alignItems: 'flex-start',
                  mb: 1.25,
                  textIndent: { xs: '-30px', sm: 0 },
                  textAlign: 'left',
                }}
              >
                <CheckCircleRoundedIcon
                  sx={{
                    mr: 1,
                    color: '#1da94a',
                    mb: { xs: -0.8, sm: 0 },
                  }}
                />
                Without State Income Tax Withheld:&nbsp;
                <span style={{ fontWeight: 700, color: '#da4b4b' }}>
                  March 15, 2025
                </span>
              </Box>
              <Box
                component="li"
                sx={{
                  display: { xs: 'inline-block', sm: 'flex' },
                  alignItems: 'flex-start',
                  mb: 1.25,
                  textIndent: { xs: '-30px', sm: 0 },
                  textAlign: 'left',
                }}
              >
                <CheckCircleRoundedIcon
                  sx={{
                    mr: 1,
                    color: '#1da94a',
                    mb: { xs: -0.8, sm: 0 },
                  }}
                />
                Form 1099-K:&nbsp;{' '}
                <span style={{ fontWeight: 700, color: '#da4b4b' }}>
                  April 30, 2025
                </span>{' '}
                (30 days after the federal deadline)
              </Box>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: 'center',
          pb: '24px !important',
        }}
      >
        <Box sx={{ px: { xs: 1, sm: 3 }, mt: 1 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2, md: 4 }}
          >
            <Button
              variant="outlined"
              size="medium"
              // className="btn-primary"
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: 4,
                display: 'inline-block',
                fontSize: { xs: 17, sm: 18, md: 18, lg: 20 },
                border: '1px solid #1da94a',
                color: '#1da94a',
                transition: 'all 0.5s ease-in-out',

                '&:hover': {
                  backgroundColor: '#1da94a',
                  color: '#fff',
                  border: '1px solid #1da94a',
                },
              }}
              href="/"
            >
              Learn More
            </Button>
            <Button
              variant="contained"
              size="medium"
              // className="btn-primary"
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                borderRadius: 4,
                display: 'inline-block',
                fontSize: { xs: 17, sm: 18, md: 18, lg: 20 },
              }}
              href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=form1099al`}
            >
              File Now
            </Button>
          </Stack>
        </Box>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default Popup1099StateAl;
